import React, { useCallback, useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronRight, faPlay, faRotateRight } from "@fortawesome/free-solid-svg-icons"

import { useBaseCheck } from "hooks/api/useBaseCheck"
import { notify } from "shared/ui/Notifications/notify"
import { HashChip } from "shared/ui/HashChip/HashChip.component"
import ProgressModal from "shared/ui/ProgressModal/ProgressModal.component"
import CoverageNotice from "features/DataChecks/components/CoverageNotice.component"
import QuestionDetailModal from "features/DataChecks/components/QuestionDetailModal.component"
import {
  BASE_CHECK_BUCKET_ORDER,
  BaseCheckBucket,
  BaseCheckTone,
  BUCKET_COPY,
  bucketQuestions,
  formatCoverageLabel,
  hasTabSpecCondition,
  questionsWithoutTabSpecCondition,
  summariseDiagnostics,
  TONE,
  toneForBucket,
} from "features/DataChecks/components/baseCheckFormat"
import type { BaseCheckQuestion, BaseCheckResult } from "types/dataChecks.types"

interface BaseCheckReportProps {
  projectId: string
  owner: string
}

const Stat: React.FC<{
  label: string
  value: string
  hint: string
  tone?: BaseCheckTone
}> = ({ label, value, hint, tone }) => (
  <div
    className={`rounded-lg border p-3 ${tone ? TONE[tone] : "border-slate-200 bg-slate-50 text-slate-600"}`}
  >
    <div className="text-[11px] font-semibold uppercase tracking-wide opacity-70">{label}</div>
    <div className="mt-0.5 text-2xl font-semibold leading-none">{value}</div>
    <div className="mt-1 text-[11px] leading-snug opacity-80">{hint}</div>
  </div>
)

const Bucket: React.FC<{
  bucket: BaseCheckBucket
  count: number
  children: React.ReactNode
}> = ({ bucket, count, children }) => {
  const [open, setOpen] = useState(false)
  const { label, hint } = BUCKET_COPY[bucket]

  return (
    <div className="border-t border-gray-100 first:border-t-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-gray-50"
      >
        <FontAwesomeIcon
          icon={faChevronRight}
          className={`h-3 w-3 text-gray-400 transition-transform ${open ? "rotate-90" : ""}`}
        />
        <span
          className={`shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium ${TONE[toneForBucket(bucket)]}`}
        >
          {label} · {count}
        </span>
        <span className="text-xs text-gray-400">{hint}</span>
      </button>
      {open && <div className="bg-gray-50 px-3 py-2">{children}</div>}
    </div>
  )
}

/** One question inside a bucket, with the figure that put it there. */
const QuestionRow: React.FC<{
  question: BaseCheckQuestion
  bucket: BaseCheckBucket
  onOpen: (question: BaseCheckQuestion) => void
}> = ({ question, bucket, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(question)}
    className="flex w-full flex-col items-start gap-0.5 border-b border-gray-100 px-2 py-1.5 text-left last:border-b-0 hover:bg-white"
  >
    <span className="flex w-full flex-wrap items-baseline gap-x-2">
      <span className="text-[13px] text-slate-800">{question.label || question.external}</span>
      <span className="font-mono text-[11px] text-slate-400">{question.external}</span>
      {bucket === "offPath" && (
        <span className="text-[12px] font-medium text-rose-700">
          {question.offPath.toLocaleString()} off path
        </span>
      )}
      {bucket === "baseGaps" && (
        <span className="text-[12px] font-medium text-amber-700">
          {question.missingOnPath.toLocaleString()} asked and blank
        </span>
      )}
      {bucket === "undetermined" && (
        <span className="text-[12px] font-medium text-violet-700">
          {question.undetermined.toLocaleString()} undetermined
        </span>
      )}
      {/* Coverage is about the question's tables, not about this check: the row still carries
          its findings, and the note says what a tab spec can do with it. */}
      {!hasTabSpecCondition(question.coverage) && (
        <span className="text-[11px] text-slate-500">
          {formatCoverageLabel(question.coverage)}
        </span>
      )}
      {/* GATE — `cleanable` is the only thing that may be labelled cleanable, and it is a
          presentation flag, never a measurement. */}
      {bucket === "offPath" && (
        <span className="text-[11px] text-slate-500">
          {question.cleanable
            ? "cleanable"
            : question.kind === "data"
              ? "data variable — never cleaned"
              : question.kind === "hidden"
                ? "hidden — reported, but not offered to cleaning in this version"
                : "not offered to cleaning"}
        </span>
      )}
    </span>
  </button>
)

/**
 * Why the check did not run, in the report's own terms. Every status returns the full shape, so
 * this is a branch on `status` — never on which fields happen to be populated.
 *
 * Exported for its own tests: these are the sentences a user sees instead of a report.
 */
export const statusHeadline = (report: BaseCheckResult): string => {
  switch (report.status) {
    case "ok":
      return "The check ran."
    case "no_qnr":
      return "This project has no questionnaire, so there is no routing to derive an asked base from."
    case "no_data":
      // GATE — `hasData` separates "there is none" from "there is one and we could not read it".
      // Telling a user their project has no data while the project shows a data file is worse
      // than saying nothing.
      return report.hasData
        ? "A data file is on this project, but the query engine could not load it — so there was nothing to check against."
        : "There is no data on this project to check against."
    case "parse_failed":
      return "The questionnaire could not be read, so no routing could be walked."
    case "script_failed":
      return "The engine could not read the data file out for the check."
    case "refused":
      // The walker's own sentence is shown under this line (`error`): it names what it cannot
      // follow, which is the part the user can act on.
      return "The check can't walk this questionnaire's routing."
  }
}

/**
 * The base-check report: what the questionnaire's routing says should have been asked, against
 * what the data actually carries.
 *
 * Every question is walked, so every question's figures are measurements. The stat band gives
 * the honesty figure (undetermined) the same weight as the actionable one (off path), says how
 * many questions' asked condition can't be shown to tab specs (a note about tables, not about
 * this check), and the footer stamps the inputs so a screenshot identifies itself.
 *
 * @component
 */
const BaseCheckReport: React.FC<BaseCheckReportProps> = ({ projectId, owner }) => {
  // On demand: `useBaseCheck` is `enabled: false`, so nothing runs until the button is pressed.
  // The answer survives in the react-query cache for a few minutes after this tab unmounts.
  const { data: report, isFetching, isError, refetch } = useBaseCheck(projectId, owner)
  const [selected, setSelected] = useState<BaseCheckQuestion | null>(null)

  const run = useCallback((): void => {
    void refetch().then((result) => {
      // A REQUEST failure is already notified by the QueryCache's global onError (the hook sets
      // `meta.errorTitle`); notifying again here would double the toast. The panel shows it.
      if (result.isError) return
      const answer = result.data
      if (!answer) return
      if (answer.status !== "ok") {
        notify.error("Base check could not run", statusHeadline(answer))
        return
      }
      const offPath = answer.totals.offPath
      const asked = answer.questions.length
      notify.success(
        "Base check complete",
        `${offPath.toLocaleString()} off-path answer${offPath === 1 ? "" : "s"} over ${asked.toLocaleString()} question${asked === 1 ? "" : "s"}.`
      )
    })
  }, [refetch])

  const buckets = useMemo(
    () => (report ? bucketQuestions(report) : null),
    [report]
  )
  const reportDiagnostics = useMemo(
    () => (report ? summariseDiagnostics(report.diagnostics) : []),
    [report]
  )

  // The stat band counts the questions themselves, NOT the report's `coverage` tally. The two
  // should agree, but only one of them is the set on screen; where they disagree the screen says
  // so (below) rather than showing two numbers for one thing.
  const questionCount = report ? report.questions.length : 0
  const noTabSpecCondition = report ? questionsWithoutTabSpecCondition(report).length : 0
  const coverageTallyDiffers = report ? report.coverage.total !== questionCount : false

  return (
    <div className="mx-auto max-w-6xl p-6">
      <ProgressModal
        title="Running the base check"
        isLoading={isFetching}
        duration={0}
        indeterminate
        subtitle="Reading the data file, then walking every respondent through the questionnaire's routing. A large tracker can take a few minutes."
      />

      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="m-0 text-[15px] font-semibold text-slate-800">Base check</h3>
          <p className="m-0 mt-0.5 text-[12px] text-slate-500">
            Who the routing says should have been asked each question, against who answered it.
          </p>
        </div>
        <button
          type="button"
          onClick={run}
          disabled={isFetching}
          className="dq-btn dq-btn--primary shrink-0"
        >
          <FontAwesomeIcon icon={report ? faRotateRight : faPlay} />
          {report ? "Run again" : "Run base check"}
        </button>
      </div>

      {isError && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-[12px] text-rose-700">
          The check could not be reached — the request failed before any report came back. Nothing
          on this project changed; try running it again.
        </div>
      )}

      {!report && !isError && (
        <div className="mb-4 rounded-lg border border-dashed border-slate-200 p-6 text-center text-[13px] text-slate-500">
          This check runs on demand. It walks the questionnaire&apos;s routing and reads the whole
          data file, so nothing happens until you run it — and the result is kept for a few
          minutes after you leave this tab.
        </div>
      )}

      {report && report.status !== "ok" && (
        <div className="mb-4 rounded-lg border border-slate-200 p-4">
          <p className="m-0 text-[13px] font-medium text-slate-700">{statusHeadline(report)}</p>
          {report.error && (
            <p className="m-0 mt-1 break-words text-[12px] text-slate-500">{report.error}</p>
          )}
          {report.status === "refused" && report.refusal?.where && (
            <p className="m-0 mt-1 break-words font-mono text-[11px] text-slate-400">
              {report.refusal.where}
            </p>
          )}
          {report.status === "script_failed" && report.log.length > 0 && (
            <div className="mt-2">
              {/* The LAST line is the failure — the rest is the trace that led to it. */}
              <p className="m-0 font-mono text-[11px] text-rose-700">
                {report.log[report.log.length - 1]}
              </p>
              <details className="mt-1">
                <summary className="cursor-pointer text-[11px] text-slate-500">
                  Engine trace ({report.log.length} lines)
                </summary>
                <pre className="m-0 mt-1 max-h-60 overflow-auto whitespace-pre-wrap break-all rounded bg-slate-50 p-2 font-mono text-[11px] text-slate-600">
                  {report.log.join("\n")}
                </pre>
              </details>
            </div>
          )}
          <p className="m-0 mt-2 text-[12px] text-slate-500">
            Nothing was measured on this run, so there is no report to show — and an absence of
            findings is not a clean bill.
          </p>
        </div>
      )}

      {report && report.status === "ok" && buckets && (
        <div>
          {/* `role="status"` is scoped to the stat band, not the whole report: the buckets below
              are collapsible, and announcing the entire report again on every toggle is what a
              screen-reader user would hear if this lived on the outer element. */}
          <div role="status" className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Stat
              label="Respondents"
              value={report.respondents.toLocaleString()}
              hint="the sample the whole report is measured against"
            />
            <Stat
              label="Questions checked"
              value={questionCount.toLocaleString()}
              hint="every question in this variant, walked respondent by respondent"
            />
            <Stat
              label="Off-path answers"
              value={report.totals.offPath.toLocaleString()}
              tone="rose"
              // `variablesAffected` counts field OCCURRENCES — one per question instance that
              // carries the field — so a looped question's column is counted once per iteration.
              // The Cleaning screen's "Variables" tile counts DISTINCT data columns and is a
              // smaller number for the same file; each says which it is rather than both saying
              // "variables".
              hint={`${report.totals.questionsWithOffPath.toLocaleString()} questions · ${report.totals.variablesAffected.toLocaleString()} variable occurrences across question instances · ${report.totals.respondentsAffected.toLocaleString()} respondents affected`}
            />
            <Stat
              label="Undetermined"
              value={report.totals.undetermined.toLocaleString()}
              tone="violet"
              // Not "the rest": a respondent undetermined on one cell of a question can be off
              // path on another, so this figure is never set against the off-path one.
              hint="a gating answer is blank or can't be read, so we cannot tell — never counted as off path, never cleaned"
            />
            <Stat
              label="Not shown to tab specs"
              value={noTabSpecCondition.toLocaleString()}
              tone="sky"
              hint="questions whose asked condition can't be shown to tab specs, so their tables use everyone who answered — they are still checked here"
            />
          </div>

          {/* The report's own tally and the questions it shipped disagree — say which one the
              figures above were counted from rather than letting two numbers stand for one thing. */}
          {coverageTallyDiffers && (
            <p className="mb-4 mt-0 text-[11px] leading-snug text-slate-500">
              The report&apos;s coverage tally counts {report.coverage.total.toLocaleString()}{" "}
              questions while it carries {questionCount.toLocaleString()}. The figures above are
              counted from the questions themselves — the ones listed below.
            </p>
          )}

          {reportDiagnostics.length > 0 && (
            <div className="mb-4 rounded-lg border border-slate-200">
              <div className="border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                What the check wants to say about this run
              </div>
              {reportDiagnostics.map((diagnostic) => (
                <div
                  key={diagnostic.code}
                  className="border-b border-slate-100 px-3 py-1.5 text-[12px] text-slate-600 last:border-b-0"
                >
                  <span className="font-mono text-[11px] text-slate-400">{diagnostic.code}</span>
                  {diagnostic.count > 1 && (
                    <span className="ml-1 text-[11px] text-slate-400">×{diagnostic.count}</span>
                  )}
                  <div className="break-words">{diagnostic.message}</div>
                </div>
              ))}
            </div>
          )}

          <div className="mb-4 overflow-hidden rounded-lg border border-gray-200">
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
              <span className="text-sm font-medium text-gray-700">
                {report.questions.length.toLocaleString()} question
                {report.questions.length === 1 ? "" : "s"} in this variant
              </span>
              <span className="ml-2 text-xs text-gray-500">
                each question is listed once, under the first of these that applies
              </span>
            </div>

            {BASE_CHECK_BUCKET_ORDER.filter(
              (bucket) => bucket !== "clean" && buckets[bucket].length > 0
            ).map((bucket) => (
              <Bucket key={bucket} bucket={bucket} count={buckets[bucket].length}>
                {buckets[bucket].map((question) => (
                  <QuestionRow
                    key={`${question.ordinal}-${question.external}`}
                    question={question}
                    bucket={bucket}
                    onOpen={setSelected}
                  />
                ))}
              </Bucket>
            ))}

            {/* Clean is a count, not a list: the questions that came back with nothing to say are
                the ones nobody needs to read. */}
            <div className="flex items-center gap-2 border-t border-gray-100 px-3 py-2">
              <span
                className={`rounded-full border px-2 py-0.5 text-xs font-medium ${TONE[toneForBucket("clean")]}`}
              >
                {BUCKET_COPY.clean.label} · {buckets.clean.length}
              </span>
              <span className="text-xs text-gray-400">{BUCKET_COPY.clean.hint}</span>
            </div>
          </div>
        </div>
      )}

      <CoverageNotice />

      {report && (
        <div className="mt-4 border-t border-slate-200 pt-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-slate-500">
            {/* The engine's echo of the flow it walked — never the variant that was requested, and
                never assumed to be a default. */}
            <span>
              Variant{" "}
              <span className="font-mono text-slate-700">
                {report.variant || "(none reported)"}
              </span>
            </span>
            <span>
              {report.respondents.toLocaleString()} respondent
              {report.respondents === 1 ? "" : "s"}
            </span>
            <span>
              {report.maskedInstances.toLocaleString()} instance
              {report.maskedInstances === 1 ? "" : "s"} found in the data
            </span>
            {report.timings && (
              <span>walked in {report.timings.totalMs.toLocaleString()} ms</span>
            )}
            <HashChip label="qnr" hash={report.qnrHash} length={12} />
            <HashChip label="data" hash={report.dataHash} length={12} />
          </div>
          <p className="m-0 mt-1.5 text-[11px] leading-snug text-slate-400">
            The questionnaire and data hashes identify what was checked: the same pair gives the
            same report.
          </p>
        </div>
      )}

      {selected && report && (
        <QuestionDetailModal
          open
          question={selected}
          examplesAvailable={report.examplesAvailable}
          reportDiagnostics={report.diagnostics}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  )
}

export default BaseCheckReport
